import { useState, useCallback, useMemo, useRef, memo } from "react";
import Icon from "../../Icon";
import api from "../../../utils/api";
import { STATUS_CFG as STATUS_CONFIG } from "../assets/Status";
import { fmtDate, initials } from "../../../utils/chatFormatters";
import {
  XIcon,
  RefreshIcon,
  SearchIcon,
  ChevronUp,
  ChevronDown,
} from "../../../utils/Icons";

const jobTitleList = (value) =>
  (Array.isArray(value) ? value : value ? [value] : [])
    .map((title) => String(title || "").trim())
    .filter(Boolean);

const formatJobTitles = (value, fallback = "—") => {
  const titles = jobTitleList(value);
  return titles.length ? titles.join(", ") : fallback;
};

/* ── BUG FIX: useSortedData – stable sort without stale-closure ──────────────── */
function useSortedData(data, defaultKey, defaultDir = "asc") {
  const [sortKey, setSortKey] = useState(defaultKey);
  const [sortDir, setSortDir] = useState(defaultDir);

  const stateRef = useRef({ sortKey, sortDir });
  stateRef.current = { sortKey, sortDir };

  const toggle = useCallback((key) => {
    const { sortKey: cur } = stateRef.current;
    if (cur === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  }, []);

  const sorted = useMemo(() => {
    if (!sortKey) return data;
    return [...data].sort((a, b) => {
      let av = a[sortKey] ?? "",
        bv = b[sortKey] ?? "";
      if (Array.isArray(av)) av = av.join(", ");
      if (Array.isArray(bv)) bv = bv.join(", ");
      if (typeof av === "string") av = av.toLowerCase();
      if (typeof bv === "string") bv = bv.toLowerCase();
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, sortKey, sortDir]);

  const SortTh = useCallback(
    ({ col, label, style }) => (
      <th
        className={`sort-th ${sortKey === col ? sortDir : ""}`}
        onClick={() => toggle(col)}
        style={style}
      >
        {label}
        {sortKey === col ? (
          sortDir === "asc" ? (
            <ChevronUp />
          ) : (
            <ChevronDown />
          )
        ) : (
          <ChevronDown />
        )}
      </th>
    ),
    [sortKey, sortDir, toggle],
  );

  return { sorted, SortTh, sortKey, sortDir };
}

/* ── Reusable FilterBar ──────────────────────────────────────────────────────── */
const FilterBar = memo(function FilterBar({
  search,
  onSearch,
  pills,
  activeFilter,
  onFilter,
  selects,
  total,
  filtered,
  onClear,
  onRefresh,
  refreshing,
  children,
}) {
  const isDirty = search !== "" || activeFilter !== "all";
  return (
    <div className="ql-filter-bar">
      <div className="ql-search-wrap">
        <SearchIcon />
        <input
          className="ql-search-input"
          type="text"
          placeholder="Search…"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
        />
      </div>

      {pills && (
        <>
          <div className="ql-divider" />
          <div className="ql-pills">
            {pills.map((p) => (
              <button
                key={p.value}
                className={`ql-pill ${p.value}${activeFilter === p.value ? " active" : ""}`}
                onClick={() => onFilter(p.value)}
              >
                {p.label}
              </button>
            ))}
          </div>
        </>
      )}

      {selects &&
        selects.map((s, i) => (
          <select
            key={i}
            className="ql-filter-select"
            value={s.value}
            onChange={(e) => s.onChange(e.target.value)}
          >
            {s.options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        ))}

      {children}

      <div className="ql-meta">
        <span className="ql-count">
          {filtered} / {total}
        </span>
        {isDirty && (
          <button className="ql-clear" onClick={onClear}>
            <XIcon /> Clear
          </button>
        )}
        {onRefresh && (
          <button
            className={`ql-refresh-btn${refreshing ? " spinning" : ""}`}
            onClick={onRefresh}
            disabled={refreshing}
          >
            <RefreshIcon /> {refreshing ? "…" : "Refresh"}
          </button>
        )}
      </div>
    </div>
  );
});

/* ── Edit User Modal ─────────────────────────────────────────────────────────── */
function EditUserModal({ user: u, onClose, onSaved, onDeleted, notify }) {
  const [form, setForm] = useState({
    name: u.name || "",
    email: u.email || "",
    role: u.role || "user",
    githubUrl: u.githubUrl || "",
    discordUrl: u.discordUrl || "",
    linkedinUrl: u.linkedinUrl || "",
    jobTitle: u.jobTitle || "",
    isVerified: !!u.isVerified,
  });
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setError("");
    if (!form.name.trim()) return setError("Name is required.");
    if (!form.email.trim()) return setError("Email is required.");
    setLoading(true);
    try {
      const { data } = await api.patch(`/users/${u._id || u.id}`, form);
      notify("User updated.");
      onSaved(data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update user.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/users/${u._id || u.id}`);
      notify("User deleted.");
      onDeleted(u._id || u.id);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete user.");
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  const sendPasswordReset = async () => {
    setResetLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: u.email });
      notify("Password reset email sent.");
    } catch {
      notify("Failed to send reset email.", "error");
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div
      className="modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="modal" style={{ maxWidth: 540, width: "100%" }}>
        <button className="modal-close" onClick={onClose}>
          <Icon name="x" size={15} />
        </button>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: ".875rem",
            marginBottom: "1.5rem",
          }}
        >
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              flexShrink: 0,
              background:
                u.role === "admin"
                  ? "linear-gradient(135deg,#f59e0b,#d97706)"
                  : u.role === "interviewer"
                    ? "linear-gradient(135deg,#7c3aed,#a78bfa)"
                    : "linear-gradient(135deg,var(--blue),#818cf8)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              color: "#fff",
              fontSize: ".9rem",
            }}
          >
            {initials(u.name || u.email)}
          </div>
          <div>
            <h2 className="modal-title" style={{ marginBottom: 2 }}>
              Edit User
            </h2>
            <p className="modal-sub" style={{ margin: 0 }}>
              {u.email}
            </p>
          </div>
        </div>

        <div className="user-edit-role-row">
          {[
            { value: "user", label: "👤 User" },
            { value: "interviewer", label: "🎙️ Interviewer" },
          ].map(({ value, label }) => (
            <button
              key={value}
              className={`user-role-btn${form.role === value ? ` active-${value}` : ""}`}
              onClick={() => setForm((f) => ({ ...f, role: value }))}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="emp-edit-grid">
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Full Name</label>
            <input
              className="form-input"
              value={form.name}
              onChange={set("name")}
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Email</label>
            <input
              className="form-input"
              type="email"
              value={form.email}
              onChange={set("email")}
            />
          </div>
        </div>

        <div className="emp-edit-grid" style={{ marginTop: ".875rem" }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Job Title</label>
            <input
              className="form-input"
              value={form.jobTitle}
              onChange={set("jobTitle")}
              placeholder="e.g. Software Engineer"
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">GitHub URL</label>
            <input
              className="form-input"
              value={form.githubUrl}
              onChange={set("githubUrl")}
              placeholder="https://github.com/…"
            />
          </div>
        </div>

        <div className="emp-edit-grid" style={{ marginTop: ".875rem" }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Discord Username</label>
            <input
              className="form-input"
              value={form.discordUrl}
              onChange={set("discordUrl")}
              placeholder="galaxy02341"
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">LinkedIn URL</label>
            <input
              className="form-input"
              value={form.linkedinUrl}
              onChange={set("linkedinUrl")}
              placeholder="linkedin.com/in/…"
            />
          </div>
        </div>

        <div className="emp-edit-toggle-row" style={{ marginTop: ".875rem" }}>
          <div>
            <div
              style={{
                fontSize: ".85rem",
                fontWeight: 600,
                color: "var(--text-1)",
              }}
            >
              Logic Signature Verified
            </div>
            <div
              style={{
                fontSize: ".76rem",
                color: "var(--text-3)",
                marginTop: 2,
              }}
            >
              User has submitted GitHub + Discord links
            </div>
          </div>
          <button
            className={`emp-toggle${form.isVerified ? " on" : ""}`}
            onClick={() =>
              setForm((f) => ({ ...f, isVerified: !f.isVerified }))
            }
          />
        </div>

        {error && (
          <div className="auth-error" style={{ marginBottom: ".875rem" }}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            {error}
          </div>
        )}

        {confirmDelete && (
          <div className="confirm-delete-bar">
            <span>Delete {u.name}? Cannot be undone.</span>
            <div style={{ display: "flex", gap: ".5rem" }}>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="btn btn-sm"
                style={{
                  background: "var(--red)",
                  color: "#fff",
                  border: "none",
                }}
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? (
                  <span className="auth-spinner" />
                ) : (
                  "Confirm Delete"
                )}
              </button>
            </div>
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: ".5rem",
            flexWrap: "wrap",
            gap: ".5rem",
          }}
        >
          <div style={{ display: "flex", gap: ".5rem" }}>
            {!confirmDelete && (
              <button
                className="btn btn-ghost btn-sm"
                style={{
                  color: "var(--red)",
                  borderColor: "rgba(239,68,68,.3)",
                }}
                onClick={() => setConfirmDelete(true)}
              >
                Delete
              </button>
            )}
            <button
              className="btn btn-ghost btn-sm"
              onClick={sendPasswordReset}
              disabled={resetLoading}
            >
              {resetLoading ? (
                <span className="auth-spinner" />
              ) : (
                "Send Reset Email"
              )}
            </button>
          </div>
          <div style={{ display: "flex", gap: ".625rem" }}>
            <button
              className="btn btn-ghost"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={save}
              disabled={loading}
            >
              {loading ? <span className="auth-spinner" /> : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Employee Detail Modal ───────────────────────────────────────────────────── */
function EmployeeDetailModal({ employee: u, onClose, onEdit }) {
  const discordStatus = u.discordActivityStatus;
  const titles = jobTitleList(u.jobTitle);

  const Row = ({ label, value }) =>
    value ? (
      <div
        style={{
          display: "flex",
          gap: ".5rem",
          padding: ".45rem 0",
          borderBottom: "1px solid var(--border)",
        }}
      >
        <span
          style={{
            fontSize: ".78rem",
            color: "var(--text-3)",
            width: 130,
            flexShrink: 0,
          }}
        >
          {label}
        </span>
        <span
          style={{
            fontSize: ".82rem",
            color: "var(--text-1)",
            wordBreak: "break-all",
          }}
        >
          {value}
        </span>
      </div>
    ) : null;

  return (
    <div
      className="modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="modal" style={{ maxWidth: 500, width: "100%" }}>
        <button className="modal-close" onClick={onClose}>
          <Icon name="x" size={15} />
        </button>

        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: ".875rem",
            marginBottom: "1.25rem",
          }}
        >
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              flexShrink: 0,
              background: "linear-gradient(135deg,var(--blue-2),#818cf8)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              color: "#fff",
              fontSize: "1rem",
            }}
          >
            {initials(u.name || u.email)}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 className="modal-title" style={{ marginBottom: 4 }}>
              {u.name || "—"}
            </h2>
            <div style={{ display: "flex", gap: ".4rem", flexWrap: "wrap" }}>
              {titles.length ? (
                titles.map((title) => (
                  <span
                    key={title}
                    className={`badge ${title === "Intern" ? "badge-blue" : "badge-gray"}`}
                    style={{ fontSize: ".7rem" }}
                  >
                    {title}
                  </span>
                ))
              ) : (
                <span className="badge badge-gray" style={{ fontSize: ".7rem" }}>
                  No title
                </span>
              )}
              {u.isVerified ? (
                <span
                  className="badge badge-green"
                  style={{ fontSize: ".7rem" }}
                >
                  ✓ Verified
                </span>
              ) : (
                <span
                  className="badge badge-gray"
                  style={{ fontSize: ".7rem" }}
                >
                  Unverified
                </span>
              )}
              {discordStatus === "active" ? (
                <span
                  className="badge badge-green"
                  style={{ fontSize: ".7rem" }}
                >
                  🟢 Discord Active
                </span>
              ) : discordStatus === "inactive" ? (
                <span className="badge badge-red" style={{ fontSize: ".7rem" }}>
                  🔴 Discord Inactive
                </span>
              ) : null}
            </div>
          </div>
        </div>

        {/* Info rows */}
        <div style={{ marginBottom: "1.25rem" }}>
          <Row label="Email" value={u.email} />
          <Row label="Phone" value={u.phone} />
          <Row label="Discord Username" value={u.discordUrl} />
          <Row
            label="GitHub"
            value={
              u.githubUrl ? (
                <a
                  href={u.githubUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "var(--blue)" }}
                >
                  {u.githubUrl}
                </a>
              ) : null
            }
          />
          <Row
            label="Joined"
            value={
              u.joinedAt
                ? new Date(u.joinedAt).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })
                : null
            }
          />
          <Row
            label="Record Created"
            value={
              u.createdAt
                ? new Date(u.createdAt).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })
                : null
            }
          />
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: ".625rem",
          }}
        >
          <button className="btn btn-ghost" onClick={onClose}>
            Close
          </button>
          <button
            className="btn btn-primary"
            onClick={() => {
              onClose();
              onEdit(u);
            }}
          >
            Edit Employee
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Edit Employee Modal ─────────────────────────────────────────────────────── */
function EditEmployeeModal({ employee, onClose, onSaved, onDeleted, notify }) {
  const [form, setForm] = useState({
    name: employee.name || "",
    email: employee.email || "",
    jobTitle: formatJobTitles(employee.jobTitle, ""),
    phone: employee.phone || "",
    githubUrl: employee.githubUrl || "",
    discordUrl: employee.discordUrl || "",
    isVerified: !!employee.isVerified,
  });
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setError("");
    if (!form.name.trim()) return setError("Name is required.");
    if (!form.email.trim()) return setError("Email is required.");
    setLoading(true);
    try {
      const { data } = await api.patch(`/employees/${employee._id}`, form);
      notify("Employee updated.");
      onSaved(data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update employee.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/employees/${employee._id}`);
      notify("Employee deleted.");
      onDeleted(employee._id);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete employee.");
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      className="modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="modal" style={{ maxWidth: 520, width: "100%" }}>
        <button className="modal-close" onClick={onClose}>
          <Icon name="x" size={15} />
        </button>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: ".875rem",
            marginBottom: "1.5rem",
          }}
        >
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              flexShrink: 0,
              background: "linear-gradient(135deg,var(--blue-2),#818cf8)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              color: "#fff",
              fontSize: ".9rem",
            }}
          >
            {initials(employee.name || employee.email)}
          </div>
          <div>
            <h2 className="modal-title" style={{ marginBottom: 2 }}>
              Edit Employee
            </h2>
            <p className="modal-sub" style={{ margin: 0 }}>
              {employee.phone ? `📞 ${employee.phone}` : "No phone on record"}
            </p>
          </div>
        </div>

        <div className="emp-edit-grid">
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Full Name</label>
            <input
              className="form-input"
              value={form.name}
              onChange={set("name")}
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Email</label>
            <input
              className="form-input"
              type="email"
              value={form.email}
              onChange={set("email")}
            />
          </div>
        </div>

        <div className="emp-edit-grid" style={{ marginTop: ".875rem" }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Job Title</label>
            <input
              className="form-input"
              value={form.jobTitle}
              onChange={set("jobTitle")}
              placeholder="Software Engineer…"
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Phone</label>
            <input
              className="form-input"
              value={form.phone}
              onChange={set("phone")}
              placeholder="+92 3xx xxxxxxx"
            />
          </div>
        </div>

        <div className="emp-edit-grid" style={{ marginTop: ".875rem" }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">GitHub URL</label>
            <input
              className="form-input"
              value={form.githubUrl}
              onChange={set("githubUrl")}
              placeholder="https://github.com/…"
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Discord Username</label>
            <input
              className="form-input"
              value={form.discordUrl}
              onChange={set("discordUrl")}
              placeholder="galaxy02341"
            />
          </div>
        </div>

        <div className="emp-edit-toggle-row" style={{ marginTop: ".875rem" }}>
          <div>
            <div
              style={{
                fontSize: ".85rem",
                fontWeight: 600,
                color: "var(--text-1)",
              }}
            >
              Logic Signature Verified
            </div>
            <div
              style={{
                fontSize: ".76rem",
                color: "var(--text-3)",
                marginTop: 2,
              }}
            >
              Employee has completed their GitHub + Discord profile
            </div>
          </div>
          <button
            className={`emp-toggle${form.isVerified ? " on" : ""}`}
            onClick={() =>
              setForm((f) => ({ ...f, isVerified: !f.isVerified }))
            }
          />
        </div>

        {error && (
          <div className="auth-error" style={{ marginBottom: ".875rem" }}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            {error}
          </div>
        )}

        {confirmDelete && (
          <div className="confirm-delete-bar">
            <span>Delete {employee.name}? Cannot be undone.</span>
            <div style={{ display: "flex", gap: ".5rem" }}>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="btn btn-sm"
                style={{
                  background: "var(--red)",
                  color: "#fff",
                  border: "none",
                }}
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? (
                  <span className="auth-spinner" />
                ) : (
                  "Confirm Delete"
                )}
              </button>
            </div>
          </div>
        )}

        <div className="emp-edit-actions">
          {!confirmDelete && (
            <button
              className="btn btn-ghost btn-sm"
              style={{ color: "var(--red)", borderColor: "rgba(239,68,68,.3)" }}
              onClick={() => setConfirmDelete(true)}
            >
              Delete
            </button>
          )}
          <button
            className="btn btn-ghost"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button className="btn btn-primary" onClick={save} disabled={loading}>
            {loading ? <span className="auth-spinner" /> : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Application Detail Modal ────────────────────────────────────────────────── */
function ApplicationDetailModal({ app, onClose, onStatusChange, notify }) {
  const [status, setStatus] = useState(app.status);
  const [saving, setSaving] = useState(false);

  const save = async (s) => {
    setSaving(true);
    try {
      await onStatusChange(app._id, s);
      setStatus(s);
      notify(`Status updated to ${s}.`);
    } catch {
      notify("Failed to update status.", "error");
    } finally {
      setSaving(false);
    }
  };

  const fieldRow = (label, value) => (
    <div key={label}>
      <div
        style={{
          fontSize: ".68rem",
          fontWeight: 700,
          letterSpacing: ".07em",
          textTransform: "uppercase",
          color: "var(--text-3)",
          marginBottom: 2,
        }}
      >
        {label}
      </div>
      <div
        style={{ fontSize: ".875rem", color: "var(--text-1)", fontWeight: 500 }}
      >
        {value || "—"}
      </div>
    </div>
  );

  return (
    <div
      className="modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="modal" style={{ maxWidth: 560, width: "100%" }}>
        <button className="modal-close" onClick={onClose}>
          <Icon name="x" size={15} />
        </button>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: ".875rem",
            marginBottom: "1.25rem",
          }}
        >
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              flexShrink: 0,
              background: "linear-gradient(135deg,var(--blue),#818cf8)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              color: "#fff",
              fontSize: ".9rem",
            }}
          >
            {initials(app.userName || app.userEmail)}
          </div>
          <div>
            <h2 className="modal-title" style={{ marginBottom: 2 }}>
              {app.userName}
            </h2>
            <p className="modal-sub" style={{ margin: 0 }}>
              {app.userEmail}
            </p>
          </div>
          <span
            className={`badge ${STATUS_CONFIG[status]?.cls || "badge-gray"}`}
            style={{ marginLeft: "auto" }}
          >
            {STATUS_CONFIG[status]?.label}
          </span>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: ".875rem",
            marginBottom: "1rem",
          }}
        >
          {fieldRow("Position", app.jobTitle)}
          {fieldRow("Experience", app.experience)}
          {fieldRow("Phone", app.phone)}
          {fieldRow("Applied", fmtDate(app.appliedAt))}
        </div>

        {(app.linkedin || app.portfolio) && (
          <div
            style={{ display: "flex", gap: ".625rem", marginBottom: "1rem" }}
          >
            {app.linkedin && (
              <a
                href={app.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-ghost btn-sm"
              >
                LinkedIn ↗
              </a>
            )}
            {app.portfolio && (
              <a
                href={
                  app.portfolio.startsWith("http")
                    ? app.portfolio
                    : `https://${app.portfolio}`
                }
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-ghost btn-sm"
              >
                Portfolio ↗
              </a>
            )}
          </div>
        )}

        {app.coverLetter && (
          <div style={{ marginBottom: "1rem" }}>
            <div
              style={{
                fontSize: ".68rem",
                fontWeight: 700,
                letterSpacing: ".07em",
                textTransform: "uppercase",
                color: "var(--text-3)",
                marginBottom: ".5rem",
              }}
            >
              Cover Letter
            </div>
            <div
              style={{
                background: "var(--bg-1)",
                border: "1px solid var(--line)",
                borderLeft: "3px solid var(--blue)",
                borderRadius: "0 8px 8px 0",
                padding: ".875rem 1rem",
                fontSize: ".875rem",
                color: "var(--text-2)",
                lineHeight: 1.7,
                whiteSpace: "pre-wrap",
                maxHeight: 200,
                overflowY: "auto",
              }}
            >
              {app.coverLetter}
            </div>
          </div>
        )}

        <div style={{ borderTop: "1px solid var(--line)", paddingTop: "1rem" }}>
          <div
            style={{
              fontSize: ".78rem",
              fontWeight: 600,
              color: "var(--text-2)",
              marginBottom: ".625rem",
            }}
          >
            Update Status
          </div>
          <div style={{ display: "flex", gap: ".5rem", flexWrap: "wrap" }}>
            {["pending", "reviewed", "accepted", "rejected"].map((s) => {
              const isActive = status === s;
              const colorMap = {
                accepted: "var(--green)",
                rejected: "var(--red)",
                reviewed: "var(--blue)",
                pending: "var(--amber)",
              };
              const bgMap = {
                accepted: "rgba(34,197,94,.15)",
                rejected: "rgba(239,68,68,.15)",
                reviewed: "rgba(79,142,247,.15)",
                pending: "rgba(245,158,11,.15)",
              };
              const borderMap = {
                accepted: "rgba(34,197,94,.4)",
                rejected: "rgba(239,68,68,.4)",
                reviewed: "rgba(79,142,247,.4)",
                pending: "rgba(245,158,11,.4)",
              };
              return (
                <button
                  key={s}
                  disabled={saving || isActive}
                  onClick={() => save(s)}
                  className="btn btn-sm"
                  style={{
                    background: isActive ? bgMap[s] : "rgba(255,255,255,.04)",
                    borderColor: isActive ? borderMap[s] : "var(--line-2)",
                    color: isActive ? colorMap[s] : "var(--text-2)",
                  }}
                >
                  {saving && !isActive ? "…" : STATUS_CONFIG[s]?.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Analytics Tab ───────────────────────────────────────────────────────────── */
const AnalyticsTab = memo(function AnalyticsTab({
  jobs,
  applications,
  employees,
  users,
}) {
  const totalApps = applications.length;

  const byStatus = useMemo(() => {
    const m = { pending: 0, reviewed: 0, accepted: 0, rejected: 0 };
    applications.forEach((a) => {
      if (m[a.status] !== undefined) m[a.status]++;
    });
    return m;
  }, [applications]);

  const byJob = useMemo(() => {
    const m = {};
    applications.forEach((a) => {
      m[a.jobTitle] = (m[a.jobTitle] || 0) + 1;
    });
    return Object.entries(m)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [applications]);

  const acceptRate =
    totalApps > 0 ? Math.round((byStatus.accepted / totalApps) * 100) : 0;
  const verifiedEmp = employees.filter((e) => e.isVerified).length;
  const barColor = {
    pending: "#f59e0b",
    reviewed: "#4f8ef7",
    accepted: "#22c55e",
    rejected: "#ef4444",
  };

  return (
    <>
      <div className="page-header">
        <h1>Analytics</h1>
        <p>A breakdown of hiring pipeline and platform metrics.</p>
      </div>

      <div className="analytics-grid">
        <div className="analytics-card">
          <div className="analytics-card-label">Total Applications</div>
          <div className="analytics-card-num">{totalApps}</div>
          <div className="analytics-card-sub">
            {byStatus.pending} pending review
          </div>
          <div className="analytics-bar-wrap">
            {Object.entries(byStatus).map(([s, n]) => (
              <div key={s} className="analytics-bar-row">
                <div className="analytics-bar-label">
                  {STATUS_CONFIG[s]?.label}
                </div>
                <div className="analytics-bar-track">
                  <div
                    className="analytics-bar-fill"
                    style={{
                      width: totalApps > 0 ? `${(n / totalApps) * 100}%` : "0%",
                      background: barColor[s],
                    }}
                  />
                </div>
                <div className="analytics-bar-val">{n}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="analytics-card">
          <div className="analytics-card-label">Acceptance Rate</div>
          <div className="analytics-card-num">{acceptRate}%</div>
          <div className="analytics-card-sub">
            {byStatus.accepted} of {totalApps} applicants accepted
          </div>
        </div>

        <div className="analytics-card">
          <div className="analytics-card-label">Employees</div>
          <div className="analytics-card-num">{employees.length}</div>
          <div className="analytics-card-sub">
            {verifiedEmp} verified · {employees.length - verifiedEmp} pending
            signature
          </div>
        </div>

        <div className="analytics-card">
          <div className="analytics-card-label">Registered Users</div>
          <div className="analytics-card-num">{users.length}</div>
          <div className="analytics-card-sub">
            {jobs.filter((j) => j.active).length} active job postings
          </div>
        </div>
      </div>

      {byJob.length > 0 && (
        <div className="card" style={{ marginBottom: "1.25rem" }}>
          <h3 className="card-title" style={{ marginBottom: "1rem" }}>
            Top Positions by Applications
          </h3>
          <div
            style={{ display: "flex", flexDirection: "column", gap: ".5rem" }}
          >
            {byJob.map(([title, count]) => (
              <div key={title} className="analytics-bar-row">
                <div
                  className="analytics-bar-label"
                  style={{
                    minWidth: 160,
                    maxWidth: 160,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: ".84rem",
                  }}
                >
                  {title}
                </div>
                <div className="analytics-bar-track">
                  <div
                    className="analytics-bar-fill"
                    style={{
                      width: `${(count / byJob[0][1]) * 100}%`,
                      background: "var(--blue)",
                    }}
                  />
                </div>
                <div className="analytics-bar-val">{count}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
});

export {
  FilterBar,
  EditUserModal,
  useSortedData,
  formatJobTitles,
  EmployeeDetailModal,
  EditEmployeeModal,
  ApplicationDetailModal,
  AnalyticsTab,
};
